import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ErrorBoundary } from 'react-error-boundary'
import { isEmpty, pipe } from 'remeda'
import type { EmptyObject } from 'type-fest'
import { useLocalStorage } from 'usehooks-ts'
import { CarouselsStack } from '@components/CarouselsStack/CarouselsStack'
import { CartDrawer } from '@components/CartDrawer/CartDrawer'
import { CategoriesGrid } from '@components/CategoriesGrid/CategoriesGrid'
import Header from '@components/Header'
import renderLiquidLinks from '@components/Header/utils/renderLiquidLinks'
import { Hero } from '@components/Hero/Hero'
import { InstagramFeed } from '@components/InstagramFeed/InstagramFeed'
import type { InstagramFeedProps } from '@components/InstagramFeed/InstagramFeed.types'
import { WishlistDrawer } from '@components/WishlistDrawer/WishlistDrawer'
import {
  withApollo,
  withErrorBoundary,
  withExchangeRates,
  withI18next,
  withSkeletonTheme,
  withStaffPickImage,
  withTheme,
} from '@hocs'
import { useQueryParams } from '@velasca/hooks/use-query-params'
import type { CartDrawerData, MacroCategoriesCardsProps } from '@velasca/types'
import { AboutUsBlock } from '@velasca/ui/about-us-block'
import { BottegasBlock } from '@velasca/ui/bottegas-block'
import { CollectionFocusBlock } from '@velasca/ui/collection-focus-block'
import { ErrorFallback } from '@velasca/ui/error-fallback'
import { HeroPlaceholder } from '@velasca/ui/hero-placeholder'
import { RedirectBlock } from '@velasca/ui/redirect-block'
import { getCurrencyByCountryCode } from '@velasca/utils/get-currency-by-country-code'
import { $settings } from '@velasca/utils/globals'
import { withWishlist } from '../hocs/withWishlist'

interface HomepageAppParams {
  heroData: any
  collectionFocusSlidesData: any
  carouselsStackData: any
  categoriesGridData: any
  aboutUsData: any
  bottegasBlockData: any
  redirectBlockData: any
  cartDrawerData: CartDrawerData
  headerEl: HTMLElement | null
  headerData: any
  promotionalBannerData: any
  categoriesBlocksData: MacroCategoriesCardsProps
  instagramFeedData: InstagramFeedProps | EmptyObject
  minisearchData: any
}

const HomePageApp = ({
  heroData,
  collectionFocusSlidesData,
  carouselsStackData,
  categoriesGridData,
  aboutUsData,
  bottegasBlockData,
  redirectBlockData,
  cartDrawerData,
  headerEl,
  headerData,
  promotionalBannerData,
  categoriesBlocksData,
  instagramFeedData,
  minisearchData,
}: HomepageAppParams) => {
  const [deliveryCountryByQueryParam] = useQueryParams<string>(
    'deliveryCountry',
    {
      type: 'string',
    },
  )

  const [, setSelectedCountryByPreference] = useLocalStorage(
    'selectedCountryByPreference',
    '',
  )

  const [, setCurrencyByUserPreference] = useLocalStorage<string | null>(
    'currency',
    null,
  )

  const [isCurrencyAutomaticallySelected, setIsCurrencyAutomaticallySelected] =
    useLocalStorage<boolean | null>('isCurrencyAutomaticallySelected', null)

  useEffect(() => {
    if (deliveryCountryByQueryParam) {
      setSelectedCountryByPreference(deliveryCountryByQueryParam)

      if (
        $settings.store_nation?.toLowerCase() === 'row' &&
        isCurrencyAutomaticallySelected !== false
      ) {
        const currency = getCurrencyByCountryCode(deliveryCountryByQueryParam)

        setCurrencyByUserPreference(currency)
        setIsCurrencyAutomaticallySelected(true)
      }
    }
  }, [
    deliveryCountryByQueryParam,
    setSelectedCountryByPreference,
    setCurrencyByUserPreference,
    isCurrencyAutomaticallySelected,
    setIsCurrencyAutomaticallySelected,
  ])

  return (
    <>
      <CartDrawer
        emptyCartCtas={cartDrawerData?.ctas || []}
        editorialSuggestedProductsDesktopImage={
          cartDrawerData?.editorial?.suggestedProductsDesktopImage
        }
      />
      <WishlistDrawer />
      {headerEl &&
        createPortal(
          <Header
            promotionalBanner={promotionalBannerData || {}}
            miscHeaderData={headerData?.miscData}
            minisearch={minisearchData}
            lowerMenu={headerData?.mobileOnlyNav}
            menu={
              renderLiquidLinks(
                headerData?.nav,
                headerData?.miscData?.liquidLinks,
              ) ?? undefined
            }
          />,
          headerEl,
        )}
      <ErrorBoundary FallbackComponent={HeroPlaceholder}>
        <Hero {...heroData} />
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <CategoriesGrid
          title={null}
          mobileLayout="singleColumn"
          cards={categoriesBlocksData.imageCards ?? []}
        />
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <CollectionFocusBlock slides={collectionFocusSlidesData} />
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <CarouselsStack {...carouselsStackData} />
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <CategoriesGrid {...categoriesGridData} />
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <AboutUsBlock {...aboutUsData} titleAs="h1" />
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <BottegasBlock {...bottegasBlockData} />
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <RedirectBlock {...redirectBlockData} />
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        {!isEmpty(instagramFeedData) && (
          /* @ts-expect-error */
          <InstagramFeed {...instagramFeedData} />
        )}
      </ErrorBoundary>
    </>
  )
}

export const HomePageAppWrapper = pipe(
  HomePageApp,
  withStaffPickImage,
  withExchangeRates,
  withWishlist,
  withSkeletonTheme,
  withI18next,
  withApollo,
  withErrorBoundary,
  withTheme,
)
